// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState } from "@awc/App/Store/Reducers";
import CartLinesList, { CartLinesListStyles } from "@awc/Overrides/Components/CartLinesList";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { getDefaultPageSize, getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCartState, getCurrentCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import {
    getCurrentPromotionsDataView,
    getPromotionsDataView,
} from "@insite/client-framework/Store/Data/Promotions/PromotionsSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: CustomAppState) => {
    const { cartId } = state.pages.checkoutReviewAndSubmit;
    const cartState = cartId ? getCartState(state, cartId) : getCurrentCartState(state);
    const promotionsDataView = cartId ? getPromotionsDataView(state, cartId) : getCurrentPromotionsDataView(state);

    return {
        cartState,
        promotionsDataView,
        defaultPageSize: getDefaultPageSize(state),
        settingsCollection: getSettingsCollection(state),
        // XNG-Change
        shipToEditMode: state.xCheckout.addresses.shipToEditMode,
        billToEditMode: state.xCheckout.addresses.billToEditMode,
    };
};

const mapDispatchToProps = {};

type Props = WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface CheckoutReviewAndSubmitProductListStyles {
    centeringWrapper?: InjectableCss;
    orderLinesList?: CartLinesListStyles;
    // XNG-Change
    sectionTitle?: InjectableCss;
}

export const productListStyles: CheckoutReviewAndSubmitProductListStyles = {
    centeringWrapper: {
        css: css`
            display: flex;
            justify-content: center;
            align-items: center;
            height: 300px;
        `,
    },
};

const styles = productListStyles;

const CheckoutReviewAndSubmitProductList: FC<Props> = ({
    cartState,
    promotionsDataView,
    settingsCollection,
    billToEditMode,
    shipToEditMode,
}) => {
    const [isCondensed, setIsCondensed] = useState(false);

    // XNG-Change
    if (shipToEditMode || billToEditMode) {
        return null;
    }

    if (!cartState.value || !cartState.value.cartLines || !promotionsDataView.value) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    const { productSettings } = settingsCollection;

    return (
        <>
            <Typography {...styles.sectionTitle} as="h2">
                {translate("Order Summary")}
            </Typography>
            <CartLinesList
                cart={cartState.value}
                promotions={promotionsDataView.value}
                isCondensed={isCondensed}
                onChangeIsCondensed={(event: React.SyntheticEvent<Element, Event>, value: boolean) =>
                    setIsCondensed(value)
                }
                editable={false}
                showSavingsAmount={productSettings.showSavingsAmount}
                showSavingsPercent={productSettings.showSavingsPercent}
                extendedStyles={styles.orderLinesList}
                // XNG-Change
                hideCondensedSelector={true}
                displayHeader={false}
            />
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CheckoutReviewAndSubmitProductList),
    definition: {
        displayName: "Product List",
        // XNG-Change
        allowedContexts: ["CheckoutReviewAndSubmitPage", "CheckoutShippingPage"],
        group: "Checkout - Review & Submit",
    },
};

export default widgetModule;
